/**
 * The quote composer's money arithmetic (contractor pages phase 3): the
 * success fee FinderlyFix takes on a confirmed job, what the contractor keeps,
 * and the reverse ("I want to take home $N, what do I quote?").
 *
 * Everything is integer cents. `successFeeCents` rounds exactly the way the
 * server posts the ledger entry (`Math.round(amount × bps / 10000)`), so the
 * number the composer shows is the number the statement will carry.
 */
import type { QuoteLineItemView } from '../api/types';

const BPS_DENOMINATOR = 10_000;

/** The success fee on `amountCents` at `bps` basis points (3000 = 30%). */
export function successFeeCents(amountCents: number, bps: number): number {
  if (!Number.isFinite(amountCents) || !Number.isFinite(bps) || amountCents <= 0 || bps <= 0) return 0;
  return Math.round((amountCents * bps) / BPS_DENOMINATOR);
}

/** What the contractor keeps of `amountCents` once the success fee is taken. */
export function netCents(amountCents: number, bps: number): number {
  if (!Number.isFinite(amountCents) || amountCents <= 0) return 0;
  return amountCents - successFeeCents(amountCents, bps);
}

/**
 * The smallest quote whose net (after the rounded fee) is at least
 * `targetNetCents`: `ceil(net ÷ (1 − bps/10000))`, computed in integers so
 * $7,000 at 30% is exactly $10,000 (float division gives 10000.000000000002
 * and a cent too many). NaN when the fee would take everything (bps ≥ 10000).
 */
export function grossForNet(targetNetCents: number, bps: number): number {
  if (!Number.isFinite(targetNetCents) || targetNetCents <= 0) return 0;
  if (!Number.isFinite(bps) || bps <= 0) return Math.ceil(targetNetCents);
  if (bps >= BPS_DENOMINATOR) return Number.NaN;
  const net = Math.ceil(targetNetCents);
  let gross = Math.ceil((net * BPS_DENOMINATOR) / (BPS_DENOMINATOR - bps));
  // The fee is rounded to the cent, so the exact formula can be a cent off
  // either way; net is non-decreasing in gross, so step to the smallest quote
  // that still keeps the target.
  while (netCents(gross, bps) < net) gross += 1;
  while (gross > net && netCents(gross - 1, bps) >= net) gross -= 1;
  return gross;
}

/** `bps` as a percent label: 3000 → "30%", 2750 → "27.5%". */
export function formatBps(bps: number): string {
  const pct = bps / 100;
  return `${Number.isInteger(pct) ? pct : Number(pct.toFixed(2))}%`;
}

/**
 * `$1,250.50`; whole dollars drop the cents (`$1,250`) unless `cents` is set.
 * Local on purpose: the Billing screen's formatter lands in parallel, and the
 * two are deduplicated when they merge.
 */
export function formatCents(cents: number, options: { currency?: string; cents?: boolean } = {}): string {
  const value = Number.isFinite(cents) ? cents : 0;
  const showCents = options.cents ?? value % 100 !== 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: options.currency ?? 'USD',
    minimumFractionDigits: showCents ? 2 : 0,
    maximumFractionDigits: showCents ? 2 : 0,
  }).format(value / 100);
}

/** Cents as the plain dollars an input shows: 125050 → "1250.50", 125000 → "1250". */
export function centsToDollarInput(cents: number): string {
  if (!Number.isFinite(cents) || cents <= 0) return '';
  const whole = Math.floor(cents / 100);
  const frac = cents % 100;
  return frac === 0 ? String(whole) : `${whole}.${String(frac).padStart(2, '0')}`;
}

/**
 * What a person typed as dollars → integer cents, or null when it is not an
 * amount. Accepts "1250", "1,250.50", "$1250", " $ 1,250.5 ", ".75";
 * refuses negatives, more than two decimals, and anything with letters.
 * String arithmetic, never float multiplication (19.99 × 100 = 1998.9999…).
 */
export function parseDollarsToCents(input: string): number | null {
  const cleaned = input.trim().replace(/^\$\s*/, '').replace(/,/g, '').replace(/\s+/g, '');
  const m = /^(\d*)(?:\.(\d{0,2}))?$/.exec(cleaned);
  if (!m) return null;
  const [, whole = '', frac = ''] = m;
  if (!whole && !frac) return null;
  const cents = Number(whole || '0') * 100 + Number(frac.padEnd(2, '0') || '0');
  return Number.isSafeInteger(cents) ? cents : null;
}

/** A quantity ("2", "1.5") → a positive number with at most two decimals, or null. */
export function parseQuantity(input: string): number | null {
  const cleaned = input.trim().replace(/,/g, '');
  if (!/^\d*(?:\.\d{0,2})?$/.test(cleaned) || cleaned === '' || cleaned === '.') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** A line's total: `round(qty × unit_cents)`. */
export function lineTotalCents(qty: number, unitCents: number): number {
  return Math.round(qty * unitCents);
}

/**
 * A stored line's total, whichever shape the server sent: `total_cents`, then
 * `qty × unit_cents`, then the older `amount_cents`.
 */
export function lineItemTotalCents(item: QuoteLineItemView): number {
  if (typeof item.total_cents === 'number') return item.total_cents;
  if (typeof item.qty === 'number' && typeof item.unit_cents === 'number') return lineTotalCents(item.qty, item.unit_cents);
  return typeof item.amount_cents === 'number' ? item.amount_cents : 0;
}
